import React from "react";
import { toast } from "react-toastify";
import { getColorAnalysisData } from "../../app/api/colorAnalysis";
import {
  BASE_VALUE_FOR_COLOR_ANALYSIS_LEVEL1,
  COLOR_ANALYSIS_LEVELS,
  keysToRemoveForGraphAnalysis,
  LEVEL1_BACKGROUND_COLOR,
  mappedTrendFilterType,
  MIN_VALUE_FOR_COLOR_ANALYSIS_LEVEL1,
} from "../../constants/constants";
import { DataNode } from "../../interface/Trends";
import { User } from "../../types";
import {
  capitalizeFirstLetterAndRemainingSmallForEachWord,
  getKeyFromValueForTrendQueryType,
  invertColor,
  removeKeysFromObject,
} from "../commonUtils";
import {
  getTrendFiltersForGraphs,
  getTrendFiltersFromSession,
} from "./graphHelperUtils";
import * as d3 from "d3";
import d3Tip from "d3-tip";
import jsPDF from "jspdf";
import "jspdf-autotable";

export const generateColorAnalysisS3JSON = async (user: User | null) => {
  try {
    const trendFilters = getTrendFiltersFromSession();
    if (!trendFilters || !trendFilters.color_analysis_level) return null;

    let colorForL2 = "";
    let colorForL3 = "";

    if (trendFilters?.colorAnalysis && trendFilters?.color_analysis_level) {
      if (trendFilters?.color_analysis_level === "LEVEL2") {
        colorForL2 = trendFilters?.selected_color
          ? trendFilters.selected_color
          : "";
      } else if (trendFilters?.color_analysis_level === "LEVEL3") {
        colorForL3 = trendFilters?.selected_color
          ? "#" + trendFilters.selected_color
          : "";
      }
    }
    if (trendFilters?.level2Color)
      colorForL2 = trendFilters?.level2Color.split("#")[1];
    removeKeysFromObject(trendFilters, keysToRemoveForGraphAnalysis);

    let l1Data = null;
    let l2Data = null;
    let l3Data = null;

    //l1 data
    const data = await setLevel1Data(user, trendFilters.query_type!);
    l1Data = data;

    //l2 data
    if (colorForL2) {
      const responseForL2 = await getColorAnalysisData(
        { ...trendFilters, primary_colour: colorForL2 },
        user
      );
      if (responseForL2?.data) {
        const data: DataNode = responseForL2?.data?.children;
        l2Data = data;
        // Check if l3 data exists
        const dataForL3 = data.children?.find(d => d.color === colorForL3);
        if (dataForL3) {
          l3Data = dataForL3;
        }
      }
    }

    const filter = getKeyFromValueForTrendQueryType(trendFilters.query_type!);

    return {
      filterType: filter,
      l1Data,
      l2Data,
      l3Data,
    };
  } catch (error) {
    console.error("Error while generating color analysis images", error);
    toast.error("Error while generating color analysis images", {
      hideProgressBar: true,
    });
    return null;
  }
};

export const showChart = (
  data: DataNode,
  chartRefCurrent: HTMLDivElement,
  level: COLOR_ANALYSIS_LEVELS,
  width: number,
  expandedView: boolean,
  setColor?: React.Dispatch<React.SetStateAction<string>>,
  snapshot: boolean = false
) => {
  const height = width;

  // Create a pack layout with specified size and padding
  const pack = (data: DataNode) =>
    d3
      .pack<DataNode>()
      .size([width, height])
      .padding(d => (d.children?.length === 1 && d.depth === 0 ? 0 : 3))(
      d3
        .hierarchy(data)
        .sum(d =>
          level === "LEVEL1"
            ? +d.value!
            : level === "LEVEL2"
              ? +d.relativePercentage!
              : +d.percentage
        )
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    );
  const root = pack(data);

  // Clear previous SVG elements
  d3.select(chartRefCurrent).selectAll("svg").remove();

  // Create an SVG element and set its attributes
  const svg = d3
    .select(chartRefCurrent)
    .append("svg")
    .attr("viewBox", `0 0 ${width} ${height}`)
    .attr("width", width)
    .attr("height", height)
    .attr(
      "style",
      `max-width: 100%; height: auto; display: block; margin: 0 auto; cursor: pointer;background-color: ${!expandedView && snapshot ? "#f6f6f6" : "#ffffff"};`
    );

  // Initialize d3-tip
  const tip = d3Tip()
    .attr("class", "d3-tip")
    .offset([-10, 0])
    //eslint-disable-next-line
    .html((d: any) => {
      return `<div style="border-radius: 5px; background-color: #f6f6f6; padding: 5px; border: 1px solid black; text-align:center;font-size:${expandedView ? "14px" : "12px"}">
        ${capitalizeFirstLetterAndRemainingSmallForEachWord(d.data.name)}<br>${level === "LEVEL1" ? d.data.percentage.toFixed(2) : d.data.relativePercentage.toFixed(2)}%
      </div>`;
    });

  svg.call(tip);

  // Create circles for each node in the hierarchy
  const node = svg
    .append("g")
    .attr("transform", `translate(${width / 2},${height / 2})`)
    .selectAll("circle")
    .data(root.descendants().filter(d => d.depth < 3))
    .join("circle")
    .attr(
      "fill",
      d =>
        (d.data as DataNode).color ||
        (!expandedView && snapshot ? "#f6f6f6" : "#ffffff")
    )
    .attr("fill-opacity", d => (level === "LEVEL2" && d.depth === 2 ? 0.55 : 1))
    .attr("pointer-events", d =>
      level === "LEVEL3" ? "all" : !d.children ? "none" : null
    )
    .on("mouseover", function (event, d) {
      if (d.depth > 0) {
        d3.select(this).attr(
          "stroke",
          invertColor(d.parent?.data.color || "#fff")
        );
        tip.show(d, this);
      }
    })
    .on("mouseout", function (event, d) {
      if (d.depth === 1) {
        d3.select(this).attr("stroke", null);
      } else if (d.depth === 2) {
        const parent = d.parent;
        if (parent && parent.depth === 1) {
          const parentNode = (this as SVGCircleElement)
            .parentNode as SVGElement;
          if (parentNode) {
            d3.select(parentNode)
              .selectAll("circle")
              .each(function (p) {
                if (p === parent) {
                  d3.select(this).attr("stroke", null);
                }
              });
          }
        }
      }
      tip.hide(d, this);
    })
    .on("click", function (event, d) {
      if (snapshot) return;
      if (d.depth === 1 && setColor) {
        setColor(d.data.color || "#000000");
      } else if (d.depth === 2) {
        const parent = d.parent;
        if (parent && parent.depth === 1 && setColor) {
          setColor(parent.data.color || "#000000");
        }
      }
    });

  // Create labels for nodes with depth === 1
  const label = svg
    .append("g")
    .attr("transform", `translate(${width / 2},${height / 2})`)
    .attr("pointer-events", "none")
    .attr("text-anchor", "middle")
    .selectAll("text")
    .data(root.descendants().filter(d => d.depth === 1))
    .join("text")
    .style("fill-opacity", 1)
    .style("display", "inline")
    .style("font-size", () => (expandedView ? "10px" : "8px"))
    .style("font-family", "Poppins")
    .style("font-weight", d => (level === "LEVEL2" ? "550" : "normal"))
    .style("fill", d => invertColor((d.data as DataNode).color))
    .each(function (d) {
      const element = d3.select(this);
      const words = d.data.name ? d.data.name.split(" ") : [];
      const truncatedName = truncateText(words.join(" "), d.r * 2 - 15);
      element.append("tspan").attr("x", 0).attr("dy", 0).text(truncatedName);
      element
        .append("tspan")
        .attr("x", 0)
        .attr("dy", "1.2em")
        .text(
          level === "LEVEL1"
            ? d.data.percentage.toFixed(2) + "%"
            : d.data.relativePercentage?.toFixed(2) + "%"
        );
    });

  const focus = root;
  let view: [number, number, number];
  zoomTo([focus.x, focus.y, focus.r * 2]);

  // Function to handle zooming
  function zoomTo(v: [number, number, number]) {
    const k = width / v[2];
    view = v;
    label.attr(
      "transform",
      d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`
    );
    node.attr(
      "transform",
      d => `translate(${(d.x - v[0]) * k},${(d.y - v[1]) * k})`
    );
    node.attr("r", d => d.r * k);
  }

  // Function to truncate text to fit within a given width
  function truncateText(text: string, maxWidth: number) {
    const ellipsis = "...";
    const context = document.createElement("canvas").getContext("2d");
    if (!context)
      return capitalizeFirstLetterAndRemainingSmallForEachWord(text);
    context.font = "8px Poppins";
    if (context.measureText(text).width <= maxWidth)
      return capitalizeFirstLetterAndRemainingSmallForEachWord(text);
    let truncated = text;
    while (context.measureText(truncated + ellipsis).width > maxWidth) {
      truncated = truncated.slice(0, -1);
    }
    return (
      capitalizeFirstLetterAndRemainingSmallForEachWord(truncated) + ellipsis
    );
  }
};

export const setLevel1Data = async (
  user: User | null,
  filterType: string,
  setColorAnalysisData?: React.Dispatch<React.SetStateAction<DataNode | null>>
) => {
  const trendFilters = getTrendFiltersForGraphs();
  if (!trendFilters) return null;
  const response = await getColorAnalysisData(
    {
      ...trendFilters,
      query_type: mappedTrendFilterType[filterType],
    },
    user
  );
  const data: DataNode = response?.data;
  data.color = LEVEL1_BACKGROUND_COLOR;
  formatDataForColorAnalysisLevel1(data);
  setColorAnalysisData && setColorAnalysisData(data as DataNode);
  return data;
};

export const formatDataForColorAnalysisLevel1 = (data: DataNode) => {
  let sum = 0;
  data?.children?.forEach(item => {
    // Calculate the value for the parent item
    const itemValue = Number(
      (
        MIN_VALUE_FOR_COLOR_ANALYSIS_LEVEL1 +
        Number(item?.percentage / 100) * BASE_VALUE_FOR_COLOR_ANALYSIS_LEVEL1
      ).toFixed(3)
    );
    item.value = itemValue;
    sum += itemValue;

    if (!item.children) {
      item.children = [];
    }

    while (item.children.length <= 1) {
      item.children.push({
        name: item.name,
        color: item.color,
      } as DataNode);
    }

    item.children?.forEach(child => {
      const calculatedValue = Number(
        (itemValue / (item?.children?.length || 1)).toFixed(4)
      );
      child.value = calculatedValue;
    });
  });
  data.value = sum;
};

export const handleDownloadColorAnalysis = (
  level1Data: DataNode | null,
  level2Data: DataNode | null,
  level3Data: DataNode | null,
  colorAnalysisFilterType: string,
  setDownloadLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  try {
    setDownloadLoading(true);
    const svgElements = [
      document.querySelector("#LEVEL1 svg"),
      document.querySelector("#LEVEL2 svg"),
      document.querySelector("#LEVEL3 svg"),
    ].filter((svg): svg is SVGSVGElement => svg !== null);

    if (svgElements.length === 0) {
      toast.error("No graph available to download");
      setDownloadLoading(false);
      return;
    }

    const pdf = new jsPDF("p", "pt", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const chartWidth = pageWidth * 0.8;
    const xOffset = (pageWidth - chartWidth) / 2;
    pdf.setFontSize(16);

    pdf.text("Color Analysis Graph", pageWidth / 2, 30, {
      align: "center",
    });

    pdf.setFontSize(8);

    pdf.text(`Filter Type: ${colorAnalysisFilterType}`, xOffset, 50);

    pdf.setFontSize(12);

    let currentY = 70;

    const processSVGElement = (svgElement: SVGSVGElement, index: number) => {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        const scale = 4;
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        context?.scale(scale, scale);
        context?.drawImage(img, 0, 0);

        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (img.height / img.width) * chartWidth;

        const graphTitle = `Level ${index + 1} Analysis Graph`;
        pdf.setFontSize(12);
        pdf.text(graphTitle, pageWidth / 2, currentY, {
          align: "center",
        });
        currentY += 20;

        pdf.addImage(imgData, "PNG", xOffset, currentY, chartWidth, imgHeight);
        currentY += imgHeight + 20;

        addTableToPDF(index);

        if (index < svgElements.length - 1) {
          pdf.addPage();
          currentY = 50;
        }

        if (index === svgElements.length - 1) {
          pdf.save("Color Analysis.pdf");
          setDownloadLoading(false);
        }
      };

      img.onerror = () => {
        toast.error("Error loading image from SVG data");
        setDownloadLoading(false);
      };

      img.src =
        "data:image/svg+xml;base64," +
        btoa(unescape(encodeURIComponent(svgData)));
    };

    const addTableToPDF = (index: number) => {
      let data: DataNode | null;
      let title: string;
      let tableHead: string[][];

      switch (index) {
        case 0:
          data = level1Data;
          title = "Level 1 Analysis Table";
          tableHead = [["Sr. No.", "Color", "Hexcode", "Percentage"]];
          break;
        case 1:
          data = level2Data;
          title = "Level 2 Analysis Table";
          tableHead = [["Sr. No.", "Color", "Hexcode", "Percentage"]];
          break;
        case 2:
          data = level3Data;
          title = "Level 3 Analysis Table";
          tableHead = [
            ["Sr. No.", "Color", "Color Combination", "Hexcode", "Percentage"],
          ];
          break;
        default:
          return;
      }

      if (!data) return;

      pdf.setFontSize(12);
      pdf.text(title, pageWidth / 2, currentY + 20, {
        align: "center",
      });

      const tableData = data.children?.map((row, ind) => {
        if (index === 2) {
          return [
            ind + 1,
            level3Data?.name,
            row.name,
            row.color,
            row.percentage.toFixed(2),
          ];
        } else {
          return [
            ind + 1,
            capitalizeFirstLetterAndRemainingSmallForEachWord(row.name),
            row.color,
            row.percentage.toFixed(2),
          ];
        }
      });

      // @ts-expect-error: autoTable is not recognized by TypeScript
      const { finalY } = pdf.autoTable({
        head: tableHead,
        body: tableData || [],
        startY: currentY + 40,
        theme: "grid",
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [77, 77, 77],
          fontStyle: "bold",
          halign: "center",
          padding: 2,
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
        },
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: [77, 77, 77],
          halign: "center",
          padding: 4,
          lineWidth: 0.5,
          lineColor: [0, 0, 0],
        },
        styles: {
          fontSize: 10,
          cellPadding: { top: 4, right: 2, bottom: 4, left: 2 },
        },
        tableWidth: chartWidth,
        margin: { left: xOffset },
      });

      currentY = finalY + 20;
    };

    svgElements.forEach((svgElement, index) => {
      processSVGElement(svgElement, index);
    });
  } catch (error) {
    toast.error("Error downloading PDF");
    console.error("Error downloading PDF", error);
  } finally {
    setDownloadLoading(false);
  }
};
